import { useState, useCallback } from 'react';
import { Matter } from '../types';
import { legalDocumentService, LegalDocument, DocumentUploadResult } from '../services/legalDocumentService';
import { documentsLogger } from '../utils/logger';

// Cache for documents and timestamps
interface DocumentCache {
  documents: LegalDocument[];
  timestamp: number;
  matterId: string;
}

// Cache for folders and timestamps
interface FolderCache {
  folders: Array<{ id: string; name: string; path: string; parentPath: string; createdDateTime: string; lastModifiedDateTime: string }>;
  timestamp: number;
  matterId: string;
}

// Global caches to persist across component re-renders
const documentsCache = new Map<string, DocumentCache>();
const foldersCache = new Map<string, FolderCache>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Hook for managing documents within legal matters
 * Provides document upload, download, list, and delete operations
 */
export const useDocuments = () => {
  const [documents, setDocuments] = useState<LegalDocument[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  // Prevent multiple simultaneous folder reloads
  const [isFolderReloadInProgress, setIsFolderReloadInProgress] = useState(false);

  // Use singleton service instance to prevent race conditions
  const documentService = legalDocumentService;

  /**
   * Loads all documents for a specific matter with caching
   * Returns information about whether a container was created during the process
   */
  const loadDocuments = useCallback(async (matter: Matter): Promise<{ containerCreated?: boolean }> => {
    setError(null);
    
    // Check cache first
    const cacheKey = matter.id;
    const cachedData = documentsCache.get(cacheKey);
    const now = Date.now();
    
    if (cachedData && (now - cachedData.timestamp < CACHE_DURATION)) {
      documentsLogger.debug('Using cached documents', { 
        matterId: matter.id, 
        count: cachedData.documents.length 
      });
      setDocuments(cachedData.documents);
      return { containerCreated: false };
    }
    
    setIsLoading(true);
    
    // Clear folder cache if switching to a different matter
    if (currentFolderMatterId && currentFolderMatterId !== matter.id) {
      documentsLogger.debug('Switching matters, clearing folder cache', {
        from: currentFolderMatterId,
        to: matter.id
      });
      setFolders([]);
      setCurrentFolderMatterId(null);
    }
    
    try {
      documentsLogger.debug('Fetching fresh documents', { matterId: matter.id });
      const result = await documentService.getMatterDocuments(matter);
      const { documents: docs, containerCreated } = result;
      
      // Update cache
      documentsCache.set(cacheKey, {
        documents: docs,
        timestamp: now,
        matterId: matter.id
      });
      
      console.log(`✅ Cached ${docs.length} documents for matter ${matter.id}${containerCreated ? ' (container created)' : ''}`);
      setDocuments(docs);
      
      return { containerCreated };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load documents';
      setError(errorMessage);
      console.error('Error loading documents:', err);
      return { containerCreated: false };
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Uploads a document to a matter
   */
  const uploadDocument = useCallback(async (
    file: File,
    matter: Matter,
    category?: LegalDocument['category'],
    confidentialityLevel?: LegalDocument['confidentialityLevel'],
    tags?: string[],
    folderPath?: string
  ): Promise<DocumentUploadResult> => {
    setIsLoading(true);
    setError(null);
    setUploadProgress(0);

    try {
      // Simulate upload progress for UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 100);

      const result = await documentService.uploadDocument(
        file,
        matter,
        category,
        confidentialityLevel,
        tags,
        folderPath
      );

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (result.success) {
        // Invalidate cache for this matter since we added a document
        documentsCache.delete(matter.id);
        console.log(`🗑️ Invalidated document cache for matter ${matter.id} after upload`);
        
        // Add the new document to the current list
        setDocuments(prev => [...prev, result.document]);
      } else {
        setError(result.error || 'Upload failed');
      }

      // Reset progress after a brief delay
      setTimeout(() => setUploadProgress(0), 1000);

      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Upload failed';
      setError(errorMessage);
      setUploadProgress(0);
      console.error('Error uploading document:', err);
      
      return {
        document: {} as LegalDocument,
        containerId: '',
        success: false,
        error: errorMessage
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Downloads a document
   */
  const downloadDocument = useCallback(async (doc: LegalDocument, matter: Matter) => {
    setError(null);
    
    try {
      const { blob, filename } = await documentService.downloadDocument(doc.id, matter);
      
      // Create download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Download failed';
      setError(errorMessage);
      console.error('Error downloading document:', err);
    }
  }, []);

  /**
   * Deletes a document
   */
  const deleteDocument = useCallback(async (doc: LegalDocument, matter: Matter) => {
    setError(null);
    
    try {
      await documentService.deleteDocument(doc.id, matter);
      
      // Invalidate cache for this matter since we removed a document
      documentsCache.delete(matter.id);
      console.log(`🗑️ Invalidated document cache for matter ${matter.id} after deletion`);
      
      // Remove the document from the current list
      setDocuments(prev => prev.filter(d => d.id !== doc.id));
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Delete failed';
      setError(errorMessage);
      console.error('Error deleting document:', err);
    }
  }, []);

  /**
   * Clears any errors
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Refreshes the document list for a matter (force refresh, bypasses cache)
   */
  const refreshDocuments = useCallback(async (matter: Matter) => {
    // Force refresh by clearing cache first
    documentsCache.delete(matter.id);
    console.log(`🔄 Force refreshing documents for matter ${matter.id}`);
    await loadDocuments(matter);
  }, [loadDocuments]);

  // =====================================
  // FOLDER OPERATIONS - Phase 3 Enhancement
  // =====================================

  /**
   * State for folder operations
   */
  const [currentFolderPath, setCurrentFolderPath] = useState<string>('');
  const [folders, setFolders] = useState<Array<{ id: string; name: string; path: string; parentPath: string; createdDateTime: string; lastModifiedDateTime: string }>>([]);
  const [currentFolderMatterId, setCurrentFolderMatterId] = useState<string | null>(null);

  /**
   * Creates a new folder in the current matter's container
   */
  const createFolder = useCallback(async (
    matter: Matter,
    folderName: string,
    parentPath?: string,
    conflictBehavior: 'fail' | 'replace' | 'rename' = 'rename'
  ) => {
    setIsLoading(true);
    setError(null);

    try {
      console.log(`📁 Creating folder "${folderName}" in matter ${matter.id}`);
      
      const result = await documentService.createFolder(
        matter,
        folderName,
        parentPath || currentFolderPath,
        { conflictBehavior, validateDepth: true, maxDepth: 3 }
      );

      console.log(`✅ Folder created: ${result.name} at path ${result.path}`);
      
      // Refresh folders list - reload entire tree structure for proper hierarchy
      console.log(`🔄 CREATE FOLDER: Clearing cache and refreshing for matter ${matter.id}`);
      
      // FORCE cache invalidation by clearing the cache first
      foldersCache.delete(matter.id);
      console.log(`🧹 Cleared cache for matter ${matter.id} to force fresh data fetch`);
      
      await loadFolders(matter, ''); // Load from root to refresh entire tree
      
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create folder';
      setError(errorMessage);
      console.error('Error creating folder:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [currentFolderPath]);

  /**
   * Lists folders in the current path - loads recursively for folder tree
   * @param matter Matter to load folders for
   * @param _path Folder path to load from (unused but kept for API compatibility)  
   */
  const loadFolders = useCallback(async (matter: Matter, _path: string = '') => {
    setError(null);
    
    // Check global cache first
    const cacheKey = matter.id;
    const cachedData = foldersCache.get(cacheKey);
    const now = Date.now();
    
    console.log(`📂 loadFolders called for matter ${matter.id}`);
    console.log(`📋 Cache check: ${cachedData ? 'HIT' : 'MISS'} - Age: ${cachedData ? Math.round((now - cachedData.timestamp) / 1000) : 'N/A'}s`);
    
    if (cachedData && (now - cachedData.timestamp < CACHE_DURATION)) {
      console.log(`📋 Using cached folders for matter ${matter.id} (${cachedData.folders.length} folders)`);
      setFolders(cachedData.folders);
      setCurrentFolderMatterId(matter.id);
      return cachedData.folders;
    }
    
    // Prevent multiple simultaneous folder reloads
    if (isFolderReloadInProgress) {
      console.log(`⏳ Folder reload already in progress for matter ${matter.id}, skipping duplicate request`);
      return folders; // Return current folders instead of making another API call
    }
    
    setIsFolderReloadInProgress(true);
    
    try {
      console.log(`📂 Loading folders for matter ${matter.id} - getting complete folder tree`);
      
      // Load all folders recursively to support the folder tree component
      const foldersList = await loadAllFoldersRecursively(matter);
      
      // Update both local state and global cache
      setFolders(foldersList);
      setCurrentFolderMatterId(matter.id);
      
      // Cache the folders globally
      foldersCache.set(cacheKey, {
        folders: foldersList,
        timestamp: now,
        matterId: matter.id
      });
      
      console.log(`✅ Cached ${foldersList.length} folders for matter ${matter.id}`);
      console.log(`📊 Global cache now has ${foldersCache.size} matter(s) cached:`, Array.from(foldersCache.keys()));
      return foldersList;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load folders';
      setError(errorMessage);
      console.error('Error loading folders:', err);
      throw err;
    } finally {
      setIsFolderReloadInProgress(false);
    }
  }, [isFolderReloadInProgress, folders, currentFolderMatterId]);

  /**
   * Recursively load all folders from all levels to build complete folder tree
   */
  const loadAllFoldersRecursively = useCallback(async (matter: Matter): Promise<Array<{ id: string; name: string; path: string; parentPath: string; createdDateTime: string; lastModifiedDateTime: string }>> => {
    const allFolders: Array<{ id: string; name: string; path: string; parentPath: string; createdDateTime: string; lastModifiedDateTime: string }> = [];
    const foldersToProcess: string[] = [''];  // Start with root
    const processedPaths = new Set<string>();
    
    while (foldersToProcess.length > 0) {
      const currentPath = foldersToProcess.shift()!;
      
      // Avoid infinite loops
      if (processedPaths.has(currentPath)) {
        continue;
      }
      processedPaths.add(currentPath);
      
      try {
        console.log(`🔍 Loading folders from path: "${currentPath || 'root'}"`);
        const folders = await documentService.listFolders(matter, currentPath);
        
        // Add these folders to our complete list
        allFolders.push(...folders);
        
        // Add each folder's path to the processing queue to get their subfolders
        folders.forEach(folder => {
          if (!processedPaths.has(folder.path)) {
            foldersToProcess.push(folder.path);
          }
        });
        
        console.log(`📁 Found ${folders.length} folders in "${currentPath || 'root'}":`, 
          folders.map(f => `"${f.name}"`).join(', '));
      } catch (error) {
        console.warn(`⚠️ Failed to load folders from path: "${currentPath}"`, error);
        // Continue processing other paths even if one fails
      }
    }
    
    console.log(`🎯 Recursive loading complete: ${allFolders.length} total folders found`);
    return allFolders;
  }, []);

  /**
   * Deletes a folder from the container
   */
  const deleteFolder = useCallback(async (matter: Matter, folderId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      console.log(`🗑️ Deleting folder ${folderId} from matter ${matter.id}`);
      
      await documentService.deleteFolder(matter, folderId);
      
      console.log(`✅ Folder deleted: ${folderId}`);
      
      // Only refresh folders list - don't also call loadDocuments to avoid double refresh
      console.log(`🔄 DELETE FOLDER: Clearing cache and refreshing for matter ${matter.id}`);
      
      // FORCE cache invalidation by clearing the cache first
      foldersCache.delete(matter.id);
      console.log(`🧹 Cleared cache for matter ${matter.id} to force fresh data fetch`);
      
      await loadFolders(matter, currentFolderPath);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete folder';
      setError(errorMessage);
      console.error('Error deleting folder:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [loadFolders, currentFolderPath, loadDocuments]);

  /**
   * Moves a document to a different folder
   */
  const moveDocument = useCallback(async (
    matter: Matter,
    documentId: string,
    targetPath: string,
    useIdBasedTarget: boolean = false
  ) => {
    setIsLoading(true);
    setError(null);

    try {
      console.log(`📦 Moving document ${documentId} to ${targetPath} in matter ${matter.id}`);
      
      const result = await documentService.moveDocument(matter, documentId, targetPath, useIdBasedTarget);
      
      console.log(`✅ Document moved: ${result.id}`);
      
      // Clear cache and refresh documents to reflect the move
      documentsCache.delete(matter.id);
      await loadDocuments(matter);
      
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to move document';
      setError(errorMessage);
      console.error('Error moving document:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [loadDocuments]);

  /**
   * Lists documents in a specific folder
   */
  const loadDocumentsInFolder = useCallback(async (matter: Matter, folderPath: string = '') => {
    setError(null);
    setIsLoading(true);
    
    try {
      console.log(`📋 Loading documents in folder "${folderPath || 'root'}" for matter ${matter.id}`);
      
      const docs = await documentService.listDocumentsInFolder(matter, folderPath);
      setDocuments(docs);
      setCurrentFolderPath(folderPath);
      
      console.log(`✅ Loaded ${docs.length} documents from folder`);
      return docs;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load documents from folder';
      setError(errorMessage);
      console.error('Error loading documents from folder:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Navigates to a specific folder path with optimized loading
   * Only loads documents - folders are loaded once and cached
   */
  const navigateToFolder = useCallback(async (matter: Matter, folderPath: string) => {
    const previousFolderPath = currentFolderPath;
    setError(null);
    
    // Prevent navigation if already navigating to the same path
    if (folderPath === currentFolderPath) {
      console.log(`📂 Already at folder path: "${folderPath}"`);
      return;
    }
    
    try {
      console.log(`🧭 Navigating from "${currentFolderPath || 'root'}" to "${folderPath || 'root'}"`);
      
      // Set the path immediately to prevent duplicate navigation requests
      setCurrentFolderPath(folderPath);
      
      // Only load documents - folders are already loaded by DocumentTree
      // This makes navigation super fast since we only need 1 API call
      await loadDocumentsInFolder(matter, folderPath);
      
      console.log(`✅ Navigation completed to "${folderPath || 'root'}"`);
    } catch (err) {
      // Reset to previous folder path on error to maintain UI consistency
      setCurrentFolderPath(previousFolderPath);
      
      const errorMessage = err instanceof Error ? err.message : 'Failed to navigate to folder';
      setError(errorMessage);
      console.error('Error navigating to folder:', err);
      
      // Don't throw the error to prevent UI crashes - just set error state
      // This allows the user to still interact with the UI and try again
    }
  }, [loadDocumentsInFolder, currentFolderPath]);

  return {
    // State
    documents,
    isLoading,
    error,
    uploadProgress,
    
    // Folder state
    currentFolderPath,
    folders,
    isFolderLoading: isFolderReloadInProgress,
    
    // Actions
    loadDocuments,
    uploadDocument,
    downloadDocument,
    deleteDocument,
    refreshDocuments,
    clearError,
    
    // Folder actions
    createFolder,
    loadFolders,
    deleteFolder,
    moveDocument,
    loadDocumentsInFolder,
    navigateToFolder,
    setCurrentFolderPath,
    
    // Cache management (for debugging)
    clearFolderCache: () => {
      foldersCache.clear();
      console.log('🧹 Folder cache cleared');
    },
    
    debugCacheState: () => {
      console.log('🔍 Current cache state:');
      console.log(`📊 Total cached matters: ${foldersCache.size}`);
      foldersCache.forEach((cache, matterId) => {
        const age = Math.round((Date.now() - cache.timestamp) / 1000);
        console.log(`  📁 Matter ${matterId}: ${cache.folders.length} folders (${age}s old)`);
      });
    }
  };
};

export default useDocuments;
